import React, { useRef, useState } from "react";
import { AlertTriangle, Upload, Image as ImageIcon, Trash2, Save, RotateCcw } from "lucide-react";

export const HEADER_MAX = 800;
export const FOOTER_MAX = 1600;
const MAX_BYTES = 3 * 1024 * 1024;
const TYPES = ["image/png", "image/jpeg", "image/webp"];
const EXAMPLE_HEADER = "Pullman Child Welfare Sharing Tree";
const EXAMPLE_FOOTER = "Thank you for returning unwrapped gifts (with tag attached securely) to this location by 5pm Sunday, December 6.";

export default function SharingTreeTagContentEditor({
  year, settings, onChange, canEdit, loading, saving, uploading, dirty,
  error, message, onSave, onReload, onUpload, onUseLogo,
}) {
  const fileRef = useRef(null);
  const [fileError, setFileError] = useState("");
  const [footerExampleUsed, setFooterExampleUsed] = useState(false);
  const s = settings || {};
  const header = s.header || "";
  const footer = s.footer || "";
  const disabled = !canEdit || loading || saving || uploading;
  const border = { borderColor: "var(--fb-border)" };
  const muted = { color: "var(--fb-text-muted)" };
  const field = "w-full px-3 py-2 rounded-lg border text-sm whitespace-pre-wrap disabled:opacity-60";

  function pickFile(e) {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    setFileError("");
    if (!f) return;
    if (!TYPES.includes(f.type)) { setFileError("Choose a PNG, JPEG, or WebP image."); return; }
    if (f.size > MAX_BYTES) { setFileError("Image must be 3 MB or smaller."); return; }
    onUpload && onUpload(f);
  }

  return (
    <details open className="rounded-xl border" style={border} data-testid="tag-editor">
      <summary className="px-4 py-3 cursor-pointer text-sm font-semibold flex items-center gap-2 select-none">
        Tag text and artwork
        {dirty && <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="tag-dirty">Unsaved changes</span>}
      </summary>
      <div className="px-4 pb-4 space-y-4">
        {!canEdit && !loading && (
          <p className="text-xs" style={muted} data-testid="tag-readonly">View only. You do not have permission to edit tag settings.</p>
        )}
        {error && <div role="alert" className="p-3 rounded-xl fb-status-error text-sm" data-testid="tag-error">{error}</div>}
        {message && !error && <div role="status" className="p-3 rounded-xl text-sm" style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" }} data-testid="tag-message">{message}</div>}

        {loading ? (
          <div className="space-y-3" data-testid="tag-loading" aria-busy="true">
            <div className="h-16 rounded-lg animate-pulse" style={{ background: "var(--fb-border)" }} />
            <div className="h-24 rounded-lg animate-pulse" style={{ background: "var(--fb-border)" }} />
            <div className="h-10 w-1/2 rounded-lg animate-pulse" style={{ background: "var(--fb-border)" }} />
          </div>
        ) : (<>
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label htmlFor="tag-header" className="text-sm font-semibold">Heading</label>
              <button type="button" className="text-xs underline disabled:opacity-50" disabled={disabled}
                      onClick={() => onChange({ header: EXAMPLE_HEADER })} data-testid="tag-example-header">Use example heading</button>
            </div>
            <textarea id="tag-header" rows={2} maxLength={HEADER_MAX} value={header} disabled={disabled}
                      onChange={(e) => onChange({ header: e.target.value })} className={field} style={border}
                      aria-describedby="tag-header-count" data-testid="tag-header" />
            <p id="tag-header-count" className="text-xs mt-1 text-right tabular-nums" style={muted}>{header.length}/{HEADER_MAX}</p>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label htmlFor="tag-footer" className="text-sm font-semibold">Footer</label>
              <button type="button" className="text-xs underline disabled:opacity-50" disabled={disabled}
                      onClick={() => { onChange({ footer: EXAMPLE_FOOTER }); setFooterExampleUsed(true); }} data-testid="tag-example-footer">Use example footer</button>
            </div>
            <textarea id="tag-footer" rows={4} maxLength={FOOTER_MAX} value={footer} disabled={disabled}
                      onChange={(e) => onChange({ footer: e.target.value })} className={field} style={border}
                      aria-describedby="tag-footer-count" data-testid="tag-footer" />
            <p id="tag-footer-count" className="text-xs mt-1 text-right tabular-nums" style={muted}>{footer.length}/{FOOTER_MAX}</p>
            {(footerExampleUsed || footer.includes("December 6")) && (
              <p role="note" className="text-xs flex gap-1.5 mt-1" style={{ color: "#92400E" }} data-testid="tag-example-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Review the example return date and weekday for {year} before saving.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <span className="block text-sm font-semibold">Image (optional)</span>
            {s.image_id ? (
              <p className="text-xs" style={muted} data-testid="tag-image-status">Image attached.</p>
            ) : (
              <p className="text-xs" style={muted} data-testid="tag-image-status">No image. PNG, JPEG, or WebP up to 3 MB.</p>
            )}
            <div className="flex flex-wrap gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="sr-only"
                     onChange={pickFile} disabled={disabled} data-testid="tag-upload" aria-label="Upload tag image" tabIndex={-1} />
              <button type="button" disabled={disabled} onClick={() => fileRef.current && fileRef.current.click()}
                      className="px-3 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={border}>
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload image"}
              </button>
              <button type="button" disabled={disabled} onClick={() => onUseLogo && onUseLogo()} data-testid="tag-use-logo"
                      className="px-3 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={border}>
                <ImageIcon className="w-4 h-4" /> Use PCW logo
              </button>
              {s.image_id && (
                <button type="button" disabled={disabled} onClick={() => onChange({ image_id: "" })} data-testid="tag-remove-image"
                        className="px-3 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={{ ...border, color: "var(--fb-error)" }}>
                  <Trash2 className="w-4 h-4" /> Remove image
                </button>
              )}
            </div>
            {fileError && <p role="alert" className="text-xs font-medium" style={{ color: "var(--fb-error)" }} data-testid="tag-file-error">{fileError}</p>}
            <div className="max-w-xs">
              <label htmlFor="tag-image-position" className="block text-xs font-semibold mb-1">Image placement</label>
              <select id="tag-image-position" value={s.image_position || "above"} disabled={disabled || !s.image_id}
                      onChange={(e) => onChange({ image_position: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border text-sm disabled:opacity-60" style={border} data-testid="tag-image-position">
                <option value="above">Above the tag text</option>
                <option value="below">Below the tag text</option>
              </select>
            </div>
          </div>
        </>)}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onReload} disabled={loading || saving || uploading} data-testid="tag-reload"
                  className="px-3 py-2 rounded-lg border text-sm inline-flex items-center gap-1.5 disabled:opacity-50" style={border}>
            <RotateCcw className="w-4 h-4" /> Reload saved settings
          </button>
          {canEdit && (
            <button type="button" onClick={onSave} disabled={disabled || !dirty || !onSave} data-testid="tag-save"
                    className="fb-btn-primary px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : `Save for ${year}`}
            </button>
          )}
        </div>
      </div>
    </details>
  );
}
